import { i18n, languageLookup, languages } from "simplydo/core/i18n";

import type {
  OrgNavLink,
  OrganisationFontFamily,
  OrganisationTerminology,
  SettingsOwnerOrganisation,
  SmtpServerFormState,
} from "components/admin/types";

import { type ChangeFields, type EditChange, describeValue } from "../changes";

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, options);

/** The organisation name in general settings. */
export const organisationNameFields: ChangeFields<
  Pick<SettingsOwnerOrganisation, "name">
> = { name: { kind: "text", label: "changes.organisation.name" } };

/** The platform title, welcome text and homepage text in branding. */
export const brandingTextFields: ChangeFields<
  Pick<SettingsOwnerOrganisation, "appName" | "welcomeText" | "dashboardText">
> = {
  appName: {
    kind: "text",
    label: "changes.organisation.appName",
    category: "admin.settings.branding.browserTitle.title",
  },
  welcomeText: {
    kind: "richText",
    label: "changes.organisation.welcomeText",
    category: "admin.settings.branding.welcomeText.title",
  },
  dashboardText: {
    kind: "richText",
    label: "changes.organisation.dashboardText",
    category: "admin.settings.branding.homepageText.title",
  },
};

const font = (value: string) => value;

export const fontFamilyFields: ChangeFields<OrganisationFontFamily> = {
  h1: { kind: "choice", format: font, label: "changes.organisation.font.h1" },
  h2: { kind: "choice", format: font, label: "changes.organisation.font.h2" },
  h3: { kind: "choice", format: font, label: "changes.organisation.font.h3" },
  h4: { kind: "choice", format: font, label: "changes.organisation.font.h4" },
  h5: { kind: "choice", format: font, label: "changes.organisation.font.h5" },
  body: {
    kind: "choice",
    format: font,
    label: "changes.organisation.font.body",
  },
};

/** The custom links shared by the navigation bar and admin sidebar. */
export const customLinkFields: ChangeFields<{ links: OrgNavLink[] }> = {
  links: {
    kind: "list",
    label: "changes.organisation.links",
    item: "changes.organisation.link",
    key: (link) => link.id,
    name: (link) => link.title || link.location,
  },
};

export const customTermsFields: ChangeFields<{ terms: string }> = {
  terms: { kind: "richText", label: "changes.organisation.customTerms" },
};

export const replyToFields: ChangeFields<
  Pick<SettingsOwnerOrganisation, "replyToEmail">
> = {
  replyToEmail: { kind: "text", label: "changes.organisation.replyToEmail" },
};

export const smtpFields: ChangeFields<Omit<SmtpServerFormState, "isEnabled">> =
  {
    serverHost: { kind: "text", label: "changes.organisation.smtp.serverHost" },
    serverPort: {
      kind: "text",
      format: (port: number) => String(port),
      label: "changes.organisation.smtp.serverPort",
    },
    serverSsl: {
      kind: "choice",
      format: (ssl: SmtpServerFormState["serverSsl"]) =>
        t(
          `changes.organisation.smtp.encryption.${
            ssl === "ssl" || ssl === "starttls" ? ssl : "none"
          }`,
        ),
      label: "changes.organisation.smtp.serverSsl",
    },
    name: { kind: "text", label: "changes.organisation.smtp.name" },
    emailAddress: {
      kind: "text",
      label: "changes.organisation.smtp.emailAddress",
    },
    serverUsername: {
      kind: "text",
      label: "changes.organisation.smtp.serverUsername",
    },
    // Never quoted, so the password doesn't show
    serverPassword: {
      kind: "richText",
      label: "changes.organisation.smtp.serverPassword",
    },
  };

/**
 * For forms that only track whether something changed: says the part was
 * edited when its values are back to what's saved but it's still unsaved.
 */
export function orEdited(
  changes: EditChange[],
  changed: boolean,
  label: string,
): EditChange[] {
  if (!changed || changes.length) return changes;
  return [{ text: describeValue({ kind: "richText" }, t(label), true, true) }];
}

/** Names an item and its new name, e.g. `Renamed the tag "A" to "B"`. */
export const describeRename = (item: string, from: string, to: string) =>
  t("changes.organisation.renamed", { item, from, to });

const terms = [
  "challenge",
  "challenges",
  "idea",
  "ideas",
  "impact",
  "impacts",
  "group",
  "groups",
] as const;

/**
 * Says which default term was renamed and in which languages, e.g.
 * `Set the term "challenge" to "project"`.
 */
export function describeTerminology(
  saved: OrganisationTerminology,
  current: OrganisationTerminology,
): EditChange[] {
  const languageKeys = [
    ...new Set([
      ...languages.map((language) => language.key),
      ...Object.keys(saved),
      ...Object.keys(current),
    ]),
  ];
  const changes: EditChange[] = [];
  for (const term of terms) {
    const languagesByChange = new Map<string, string[]>();
    for (const language of languageKeys) {
      const before = saved[language]?.[term] ?? "";
      const after = current[language]?.[term] ?? "";
      if (before === after) continue;
      const change = JSON.stringify([before, after]);
      languagesByChange.set(change, [
        ...(languagesByChange.get(change) ?? []),
        language,
      ]);
    }
    for (const [change, changedLanguages] of languagesByChange) {
      const [before, after] = JSON.parse(change) as [string, string];
      const everywhere = languages.every((language) =>
        changedLanguages.includes(language.key),
      );
      const label = t(
        `changes.organisation.terminology.${everywhere ? "term" : "termIn"}`,
        {
          term: t(`changes.organisation.terminology.${term}`),
          languages: changedLanguages
            .map((key) => languageLookup.get(key)?.name ?? key)
            .join(", "),
        },
      );
      changes.push({
        text: describeValue({ kind: "text" }, label, before, after),
      });
    }
  }
  return changes;
}

/**
 * Describes changes to the entries shown in a menu: which were shown or
 * hidden, and whether the rest were reordered.
 */
export function describeShownEntries<Entry extends { id: string }>(
  saved: Entry[],
  current: Entry[],
  name: (entry: Entry) => string,
  keys: { shown: string; hidden: string; reordered: string },
): EditChange[] {
  const savedIds = saved.map((entry) => entry.id);
  const currentIds = current.map((entry) => entry.id);
  const changes: EditChange[] = [
    ...current
      .filter((entry) => !savedIds.includes(entry.id))
      .map((entry) => ({ text: t(keys.shown, { name: name(entry) }) })),
    ...saved
      .filter((entry) => !currentIds.includes(entry.id))
      .map((entry) => ({ text: t(keys.hidden, { name: name(entry) }) })),
  ];
  const kept = savedIds.filter((id) => currentIds.includes(id));
  const keptNow = currentIds.filter((id) => savedIds.includes(id));
  if (kept.some((id, index) => keptNow[index] !== id)) {
    changes.push({ text: t(keys.reordered) });
  }
  return changes;
}
